import { HttpClient, HttpClientModule } from '@angular/common/http';
import {
  APP_INITIALIZER,
  enableProdMode,
  importProvidersFrom,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { firstValueFrom, of } from 'rxjs';
import { AppComponent } from './app/app.component';
import { PageStrategy } from './app/page-strategy';
import { environment } from './environments/environment';
import { CompanyService } from './app/services/company/company.service';
import { SsoService } from './app/services/sso/sso.service';
import { ADMIN } from './app/core/constants/auth.constants';

const isHealthcheck = window.location.pathname === '/up';

if (environment.production) {
  enableProdMode();
}

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function defineComponent(
  pageStrategy: PageStrategy,
  ssoService: SsoService,
  companyService: CompanyService,
): () => Promise<void> {
  return async () => {
    const params = new URLSearchParams(window.location.search);
    const invitationToken = params.get('invitationToken');
    const code = params.get('code');
    const state = params.get('state');

    if (invitationToken) {
      pageStrategy.component = pageStrategy.get('register');
      return;
    }

    if (companyService.name === ADMIN) {
      pageStrategy.component = pageStrategy.get('signin');
      return;
    }

    if (code && state) {
      params.delete('code');
      params.delete('state');
      const search = params.toString();
      const redirectUri = `${window.location.origin}${window.location.pathname}${search ? '?' + search : ''}`;
      try {
        await firstValueFrom(ssoService.loginWithSso(code, state, companyService.name, redirectUri));
      } catch (error: any) {
        ssoService.errorMessage = error?.error?.error || error?.error || 'SSO authentication failed';
        pageStrategy.component = pageStrategy.get('sso-error');
      }
      return;
    }

    try {
      const config = await firstValueFrom(ssoService.getSsoConfig(companyService.name));
      if (config.is_sso_used && config.sso_client_url) {
        ssoService.redirectToSso(config.sso_client_url);
      } else {
        pageStrategy.component = pageStrategy.get('signin');
      }
    } catch {
      pageStrategy.component = pageStrategy.get('signin');
    }
  };
}

bootstrapApplication(AppComponent, {
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: (companyService: CompanyService) => () => {
        if (isHealthcheck) return of(undefined);
        return companyService.init();
      },
      deps: [CompanyService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (pageStrategy: PageStrategy, ssoService: SsoService, companyService: CompanyService) => () => {
        if (isHealthcheck) return of(undefined);
        return defineComponent(pageStrategy, ssoService, companyService)();
      },
      deps: [PageStrategy, SsoService, CompanyService],
      multi: true,
    },
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'en',
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      }),
      HttpClientModule,
    ),
  ],
}).catch(err => console.error(err));
